import { useState, useCallback, useRef } from 'react';
import InlineAlert from '../../common/InlineAlert';
import { ListingResource, PropertyField } from '../../../system/field';
import {
  useListingField,
  useListingResource,
} from '@brightmls/nextgen.lae.flc';
import { OpenHouse } from '../control/OpenHouse';
import { OpenHouseResource } from '../../../reso/resources/OpenHouseResource';
import { ResoResourceBase, ResoResourceManager } from '../../../reso/types';
import { useModal } from '../../../contexts/ModalContext';
import { useResoResource } from '../../../reso/useResoResource';

type ResolveFn = (value: boolean) => void;
type PromiseResolver = {
  resolve: ResolveFn;
};
type CreatePromiseType = [Promise<boolean>, ResolveFn];

const createPromise = (): CreatePromiseType => {
  let resolveFn: ResolveFn = () => {};
  return [
    new Promise((resolve, reject) => {
      resolveFn = resolve;
    }),
    resolveFn,
  ];
};

// Updated interface to use hooks instead of context
interface AlertConfigHooks {
  listingKey: string;
  openHouses: OpenHouse[];
  setOpenHouses: (houses: OpenHouse[]) => void;
}

interface AlertConfigProps {
  onClose: (hooks?: AlertConfigHooks) => void;
  onAccept: (
    hooks?: AlertConfigHooks,
    resourceManager?: ResoResourceManager<ResoResourceBase>,
  ) => Promise<boolean>;
  getMessage: (hooks?: AlertConfigHooks) => string;
  shouldShowAlert: (hooks?: AlertConfigHooks) => boolean;
}

const alertConfigs: Record<string, AlertConfigProps> = {
  [PropertyField.OFFICE_EXCLUSIVE_YN]: {
    onClose: async (hooks?: AlertConfigHooks) => {
      // Here, some stuff can done when alert is closed
      return;
    },
    onAccept: async (
      hooks?: AlertConfigHooks,
      resourceManager?: ResoResourceManager<ResoResourceBase>,
    ): Promise<boolean> => {
      if (hooks && resourceManager) {
        const { listingKey, openHouses, setOpenHouses } = hooks;

        const tryToDeleteResources = async (
          listingKey: number,
          resourcesToDelete: ResoResourceBase[],
          limitToRetry: number = 5,
        ): Promise<ResoResourceBase[]> => {
          if (limitToRetry === 0) {
            if (resourcesToDelete.length) return resourcesToDelete;
            // No Resources to delete
            return [];
          }

          if (resourcesToDelete.length === 0) return [];

          const results = await Promise.all(
            resourcesToDelete.map(async (oh: ResoResourceBase) => {
              const res = await resourceManager.cwa().delete(oh);
              return { ...oh, success: res };
            }),
          );

          // Get resources that failed to be deleted (success = false)
          const failedResults: ResoResourceBase[] = results.reduce(
            (acc: ResoResourceBase[], item: any) =>
              !item.success ? [...acc, { ...item }] : acc,
            [],
          );

          if (failedResults.length === 0) return [];

          // Try again
          return await tryToDeleteResources(
            listingKey,
            failedResults,
            limitToRetry - 1,
          );
        };

        if (openHouses.length) {
          try {
            const openHousesToDelete: ResoResourceBase[] = openHouses.map(
              (oh: OpenHouse) => new OpenHouseResource(oh),
            );

            if (openHousesToDelete.length === 0) {
              setOpenHouses([]);
              return true;
            }

            await tryToDeleteResources(
              parseInt(listingKey),
              openHousesToDelete,
              5,
            );

            // set resource always
            setOpenHouses([]);
            return true;
          } catch (e) {
            // If an error happens, alert is still showing
            console.warn('Error deleting Open Houses:', e);
            return false;
          }
        }
      }

      return true;
    },
    getMessage: () => {
      return 'Open Houses aren\'t available for Office Exclusive listings. Clicking "OK" will delete your Open House';
    },
    shouldShowAlert: (hooks?: AlertConfigHooks) => {
      if (hooks) {
        return !!hooks.openHouses.length;
      }
      return false;
    },
  },
};

export const useInlineAlert = (fieldKey: PropertyField | string) => {
  // Use the magic FLC hooks! 🪄
  const { value: listingKey } = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );

  const { items: openHouses, set: setOpenHouses } =
    useListingResource<OpenHouse>(ListingResource.OpenHouse);

  const { resourceManager } = useResoResource<ResoResourceBase>(
    ListingResource.OpenHouse,
  );
  const { showModal } = useModal();
  const resolverRef = useRef<PromiseResolver>({ resolve: () => {} });
  const showInlineAlertRef = useRef<boolean>(false);
  const [showInlineAlert, setShowInlineAlert] = useState<boolean>(
    showInlineAlertRef.current,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Create hooks object for alert configs
  const alertHooks: AlertConfigHooks = {
    listingKey,
    openHouses,
    setOpenHouses,
  };

  const hasInlineAlert = useCallback(() => {
    const alertConfig: AlertConfigProps = alertConfigs[fieldKey];
    return !!alertConfig;
  }, [fieldKey]);

  const showAlert = useCallback(
    (show: boolean) => {
      showInlineAlertRef.current = show;
      setShowInlineAlert(showInlineAlertRef.current);
    },
    [setShowInlineAlert],
  );

  const getAlertResponse = useCallback(
    async (value: any) => {
      const alertConfig: AlertConfigProps = alertConfigs[fieldKey];
      if (!alertConfig) return true;

      // If alert is not shown, it continues with normal behavior
      const shouldShowAlert = alertConfig.shouldShowAlert(alertHooks);
      if (!shouldShowAlert) return true;

      const [promise, resolve]: CreatePromiseType = await createPromise();
      resolverRef.current = { resolve };

      if (value === false) return true;

      showAlert(value);

      return promise;
    },
    [alertHooks, fieldKey, showAlert],
  );

  const onAcceptHandler = useCallback(async () => {
    const alertConfig: AlertConfigProps = alertConfigs[fieldKey];
    if (!alertConfig) return;

    setIsLoading(true);
    const resolve: ResolveFn | null = resolverRef.current.resolve;
    try {
      const onAcceptResponse = await alertConfig.onAccept(
        alertHooks,
        resourceManager,
      );

      // If onAcceptResponse is true, alert is hidden
      if (resolve) resolve(onAcceptResponse);
      showAlert(!onAcceptResponse);

      if (!onAcceptResponse) {
        throw new Error('OnSuccess call failed!');
      }
    } catch (e) {
      showModal({
        // title: 'Error happened', // Optional title
        content: <div>An error happened while performing this action</div>,
        cancelText: 'Close',
      });
    }

    setIsLoading(false);
  }, [alertHooks, resourceManager, showAlert, fieldKey, showModal]);

  const onCloseHandler = useCallback(async () => {
    const alertConfig: AlertConfigProps = alertConfigs[fieldKey];
    if (!alertConfig) return true;

    const resolve: null | ResolveFn = resolverRef.current.resolve;
    if (resolve) {
      resolve(false);
    }
    alertConfig?.onClose(alertHooks);
    showAlert(false);
  }, [alertHooks, fieldKey, showAlert]);

  const inlineAlert = useCallback(() => {
    const alertConfig: AlertConfigProps = alertConfigs[fieldKey];
    if (!alertConfig) return null;

    const shouldShowAlert = alertConfig.shouldShowAlert(alertHooks);
    if (!shouldShowAlert) return null;

    const message = alertConfig.getMessage();

    return (
      <InlineAlert
        hide={!showInlineAlert}
        isLoading={isLoading}
        message={message || ''}
        onAccept={onAcceptHandler}
        onClose={onCloseHandler}
        variant="warning"
      />
    );
  }, [
    isLoading,
    alertHooks,
    onAcceptHandler,
    onCloseHandler,
    showInlineAlert,
    fieldKey,
  ]);

  return {
    getAlertResponse,
    hasInlineAlert,
    showAlert,
    inlineAlert,
  };
};
